import * as React from 'react';
import eventsApi from '../../../../apiFetcher/eventsApiFetcher';
import { useEventsDataStore } from '../EventsDataStoreContext';
import {
	Event as InitialState,
	EventSku,
	EventAction,
	OfInterestTo,
	EventCommunity,
} from './types';

export const initialState = {
	communities: [],
	description: '',
	title: '',
	images: {},
	ofInterestToList: [],
	organization: {},
	skus: [],
	links: {},
	venue: {},
	ownerAppUserId: null,
	sessionParentEventId: null,
	time: {
		startTime: '',
		endTime: '',
	},
};

export enum EventActionType {
	AddOrUpdateEvent = 'ADD_OR_UPDATE_EVENT',
	ResetState = 'RESET_STATE',
	AddSkuToEvent = 'ADD_SKU_TO_EVENT',
	RemoveSkuFromEvent = 'REMOVE_SKU_FROM_EVENT',
}

interface IEventDataStoreContext {
	state: InitialState;
	dispatch: React.Dispatch<any>;
	eventId: number | null;
	getSkusAsList: () => string[];
	getCommunitiesAsList: () => number[];
	formatOfInterestToList: (interestKeys: string[]) => OfInterestTo[];
	resetState: () => void;
}

const EventDataStoreContext = React.createContext<IEventDataStoreContext>({
	state: initialState,
	dispatch: () => null,
	getSkusAsList: () => [],
	getCommunitiesAsList: () => [],
	formatOfInterestToList: ([]) => [],
	resetState: () => {},
	eventId: null,
});

const reducer = (state: InitialState, action: EventAction) => {
	switch (action.type) {
		case EventActionType.AddOrUpdateEvent:
			return {
				...state,
				...action.payload,
			};
		case EventActionType.AddSkuToEvent:
			const isSkuUnique = !state.skus.find(
				title => title.sku === action.payload,
			);
			return {
				...state,
				skus: isSkuUnique
					? [...state.skus, { sku: action.payload }]
					: state.skus,
			};
		case EventActionType.RemoveSkuFromEvent:
			return {
				...state,
				skus: state.skus.filter(s => s.sku !== action.payload),
			};
		case EventActionType.ResetState:
			return {
				...initialState,
			};
		default:
			console.error('Unhandled action type');
			return {
				...state,
			};
	}
};

type EventDataStoreProviderProps = {
	eventId: number | null;
	children: React.ReactNode;
};

const eventRequestInitialValue = {
	promise: initialState,
	cancel: () => {},
};

export const EventDataStoreProvider = ({
	eventId,
	children,
}: EventDataStoreProviderProps) => {
	const [state, dispatch] = React.useReducer(reducer, initialState);
	const { buildEvent } = useEventsDataStore();
	const request = React.useRef(eventRequestInitialValue);

	React.useEffect(() => {
		resetState();

		return () => {
			eventsApi.revoke(request.current);
		};
	}, [eventId]);

	const fetchEvent = async () => {
		try {
			request.current = eventsApi.cancelableGet(`/api/events/${eventId}`);
			const event = await request.current.promise;

			dispatch({
				type: EventActionType.AddOrUpdateEvent,
				payload: buildEvent(event),
			});
		} catch (e) {
			console.error(e);
		}
	};

	const getSkusAsList = () => {
		return state.skus.map((title: EventSku) => title.sku);
	};

	const getCommunitiesAsList = () => {
		return state.communities.map(
			(community: EventCommunity) => community.communityId,
		);
	};

	const formatOfInterestToList = (interestKeys: string[]) => {
		return interestKeys.map(interestKey => ({ interestKey }));
	};

	const resetState = () => {
		const isEditingEvent = eventId !== null;
		return isEditingEvent
			? fetchEvent()
			: dispatch({ type: EventActionType.ResetState });
	};

	const getStore = () => ({
		state,
		dispatch,
		eventId,
		getSkusAsList,
		getCommunitiesAsList,
		formatOfInterestToList,
		resetState,
	});

	return (
		<EventDataStoreContext.Provider value={getStore()}>
			{typeof children === 'function' ? children(resetState) : children}
		</EventDataStoreContext.Provider>
	);
};

export const useEventDataStore = () => React.useContext(EventDataStoreContext);
